/**
 * Lectura de un lugar de Google Places (API nueva, `PlaceAutocompleteElement`)
 * y normalización a la forma que usan los formularios del sitio.
 *
 * Vivía adentro de `components/GoogleMaps/MapWhitAutcoComplete.jsx`, mezclado
 * con el render de un mapa de 400px. Está acá para que lo pueda usar también
 * el cotizador de paquetería, que necesita el código postal pero no el mapa.
 *
 * Reglas de este archivo:
 * - No importa React ni nada de Node: sólo depende de `window.google` y sólo
 *   dentro de las funciones que hacen red.
 * - `parsePlace` es pura y testeable. Los dos fallbacks de código postal, que
 *   sí hacen red, viven en `resolvePlaceDetails`.
 * - No inventa datos: si Google no devuelve el código postal, el campo vuelve
 *   vacío y es el formulario el que decide pedírselo al usuario.
 */

/**
 * Lee un componente de dirección por tipo.
 *
 * Tolera los dos formatos que devuelve Google: el nuevo (`longText`) y el
 * legacy (`long_name`), porque `Place.toJSON()` y `Geocoder` no coinciden.
 * @param {Array<{types?: string[], long_name?: string, longText?: string}>} components
 * @param {string} type Tipo de componente, p.ej. `'postal_code'`.
 * @returns {string} El valor, o `''`.
 */
export function getAddressComponent(components, type) {
  const list = Array.isArray(components) ? components : [];
  const match = list.find((component) => (component?.types || []).includes(type));

  return match?.long_name ?? match?.longText ?? '';
}

/**
 * Normaliza los componentes del formato nuevo al legacy, para poder mezclar en
 * una sola lista lo que devuelven `Place.fetchFields` y `Geocoder`.
 * @param {Array<object>} components
 * @returns {Array<{long_name: string, short_name: string, types: string[]}>}
 */
export function normalizeAddressComponents(components) {
  return (Array.isArray(components) ? components : []).map((component) => ({
    long_name: component?.longText || component?.long_name || '',
    short_name: component?.shortText || component?.short_name || '',
    types: component?.types || []
  }));
}

/**
 * Arma los datos de dirección a partir de un lugar ya resuelto. Pura.
 *
 * `localidad` y `calle` tienen cascada de fallbacks porque Google varía los
 * componentes según el país y según el tipo de lugar (una intersección o un
 * comercio no traen `route`).
 * @param {object} place Resultado de `Place.toJSON()`.
 * @param {Array<object>} [components] Componentes a usar; por defecto los del lugar.
 * @returns {{calle: string, numero: string, codigo_postal: string, provincia: string,
 *            localidad: string, country: string, district: string, departament: string,
 *            lat: number|undefined, lng: number|undefined}}
 */
export function parsePlace(place, components) {
  const source = place && typeof place === 'object' ? place : {};
  const list = components || source.addressComponents || [];
  const get = (type) => getAddressComponent(list, type);

  const localidad =
    get('locality') ||
    get('sublocality_level_1') ||
    get('administrative_area_level_2') ||
    get('sublocality') ||
    get('administrative_area_level_1') ||
    '';

  const displayName = source.displayName?.text || source.displayName || '';
  const formattedAddress = source.formattedAddress || '';
  const calle = get('route') || displayName || formattedAddress.split(',')[0]?.trim() || '';

  return {
    calle,
    numero: get('street_number'),
    codigo_postal: get('postal_code'),
    provincia: get('administrative_area_level_1'),
    localidad,
    country: get('country'),
    district: get('sublocality_level_2') || '',
    departament: get('administrative_area_level_2') || '',
    lat: source.location?.lat,
    lng: source.location?.lng
  };
}

/**
 * Pide los detalles del lugar para conseguir el código postal que la predicción
 * no trajo. Hace red. Nunca lanza.
 * @param {object} place Resultado de `Place.toJSON()`.
 * @returns {Promise<Array<object>>} Componentes normalizados, o `[]`.
 */
async function fetchPlaceComponents(place) {
  if (!place?.id || !window.google?.maps?.places?.Place) return [];

  try {
    const instance = new window.google.maps.places.Place({ id: place.id });

    await instance.fetchFields({ fields: ['addressComponents'] });

    return normalizeAddressComponents(instance.addressComponents);
  } catch (error) {
    console.warn('[PlaceDetails] error:', error);

    return [];
  }
}

/**
 * Geocoding inverso sobre las coordenadas del lugar: el último recurso para el
 * código postal. Hace red. Nunca lanza.
 * @param {{lat: number, lng: number}} location
 * @returns {Promise<string>} El código postal, o `''`.
 */
function reverseGeocodeZipcode(location) {
  if (!location || !window.google?.maps?.Geocoder) return Promise.resolve('');

  return new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location }, (results, status) => {
      if (status !== window.google.maps.GeocoderStatus.OK) {
        console.warn('[Geocoder] status:', status);
        resolve('');

        return;
      }

      const components = (results || []).flatMap((result) => result.address_components || []);

      resolve(getAddressComponent(components, 'postal_code'));
    });
  });
}

/**
 * Resuelve un lugar elegido en el autocomplete a los datos de dirección.
 *
 * El código postal es el dato que más falta: Google no lo devuelve en la
 * predicción para muchas direcciones argentinas. Por eso se intenta, en orden,
 * con los componentes de la predicción, con los detalles del lugar y con un
 * geocoding inverso sobre sus coordenadas. Si después de los tres sigue vacío,
 * vuelve vacío y el formulario se lo pide al usuario.
 * @param {object} place Resultado de `Place.toJSON()`.
 * @returns {Promise<ReturnType<typeof parsePlace>|null>} `null` si no hay lugar.
 */
export async function resolvePlaceDetails(place) {
  if (!place) return null;

  let components = normalizeAddressComponents(place.addressComponents);
  let codigoPostal = getAddressComponent(components, 'postal_code');

  if (!codigoPostal) {
    const detail = await fetchPlaceComponents(place);

    components = [...components, ...detail];
    codigoPostal = getAddressComponent(detail, 'postal_code');
  }

  if (!codigoPostal) {
    codigoPostal = await reverseGeocodeZipcode(place.location);
  }

  return { ...parsePlace(place, components), codigo_postal: codigoPostal };
}
